import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  input,
  Signal,
  signal,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
  RouterOutlet,
} from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { Spinner } from '../spinner/spinner';
import { filter, map, tap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SpinnerService } from '../../services/spinner.service';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { APP_PATHS } from '../../config/app-paths';

@Component({
  selector: 'app-main-auth-layout',
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    Spinner,
    SidebarComponent,
  ],
  templateUrl: './main-auth-layout.component.html',
  styleUrl: './main-auth-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainAuthLayoutComponent {
  readonly #spinnerService = inject(SpinnerService);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  public homePageUrl = input<string>();
  protected isMobile = signal(false);
  protected readonly isFirstLogin: Signal<boolean>;
  protected sidebarVisible = signal(false);
  public isApplicationContextSelectView = signal(false);

  constructor() {
    const isFirstLoginUrl = (url: string) =>
      url.includes('/' + APP_PATHS.auth.firstLogin);

    this.isFirstLogin = toSignal(
      this.#router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => isFirstLoginUrl(e.url)),
      ),
      { initialValue: isFirstLoginUrl(this.#router.url) },
    );

    this.#subscribeRouterEventsListener();
    this.#checkScreen();
  }

  @HostListener('window:resize')
  protected onResize(): void {
    this.#checkScreen();
  }

  #subscribeRouterEventsListener(): void {
    this.#router.events
      .pipe(
        tap((event: any) => this.#navigationInterceptor(event as RouterEvent)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  #navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.#spinnerService.show();
    }

    if (event instanceof NavigationEnd) {
      this.#spinnerService.hide();
      this.#checkIfActivePathIsContextSelect();
    }

    if (event instanceof NavigationCancel) {
      this.#spinnerService.hide();
    }
    if (event instanceof NavigationError) {
      this.#spinnerService.hide();
    }
  }

  #checkScreen(): void {
    this.isMobile.set(window.innerWidth < 768);
    if (!this.isMobile()) {
      this.sidebarVisible.set(true);
    } else {
      this.sidebarVisible.set(false);
    }
  }

  #checkIfActivePathIsContextSelect(): void {
    let active = this.#activatedRoute.snapshot;
    while (active.firstChild) {
      active = active.firstChild;
    }
    if (active.routeConfig?.path === APP_PATHS.auth.applicationContext)
      this.isApplicationContextSelectView.set(true);
    else this.isApplicationContextSelectView.set(false);
  }

  protected toggleSidebar(): void {
    if (this.isMobile()) {
      this.sidebarVisible.update((value) => !value);
    }
  }
}
